import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { LoginData, RegisterCompany } from "../types";
import { extractErrorMessage } from "../util";
import Cookies from "js-cookie";

// Remove the CSRF call since it’s not needed for JWT.
// axios.defaults.withCredentials = true; // Optional if not using cookies for session
const base_url = process.env.REACT_APP_BASE_URL;

interface AuthState {
  user: any | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  error?: string | null;
}

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isLoggedIn: false,
  error: null,
};
//forgot password
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, thunkAPI) => {
    try {
      const response = await axios.post(`${base_url}/forgot-password`, { email });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Reset password
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data: { token: string; password: string, 
    password_confirmation: string, email: string
   }, thunkAPI) => {
    try {
      const response = await axios.post(`${base_url}/reset-password`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Thunk to check if the user is authenticated
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, thunkAPI) => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(`${base_url}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Thunk to register a company and handle response
export const addCompany = createAsyncThunk(
  "auth/addCompany",
  async (data: RegisterCompany, thunkAPI) => {
    try {
      const response = await axios.post(`${base_url}/registerCompany`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Thunk to log in and store user data
export const login = createAsyncThunk(
    "auth/login",
    async (data: LoginData, thunkAPI) => {
        try {
            const response = await axios.post(`${base_url}/loginAdmin`, data);
            const { access_token } = response.data;

            if (!access_token) {
                return thunkAPI.rejectWithValue(response.data.message || "Token not received");
            }

            Cookies.set("token", access_token, { expires: 7, secure: true });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(extractErrorMessage(error));
        }
    }
);



export const logoutApp = createAsyncThunk(
    "auth/logout",
    async (_, thunkAPI) => {
        try {
            const token = Cookies.get("token");
            if (!token) {
                throw new Error("No token found for logout.");
            }

            // Remove the token before making the request to ensure it's not picked up by checkAuthStatus
            Cookies.remove("token");

            const response = await axios.post(
                `${base_url}/logout`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(extractErrorMessage(error));
        }
    }
);




// Redux slice for authentication
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCompany.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoggedIn = true;
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user = null;
        state.error = action.payload as string;
      })
      .addCase(logoutApp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
        .addCase(logoutApp.fulfilled, (state) => {
            return { ...initialState, isLoading: false }; // Reset all
        })

        .addCase(logoutApp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        checkAuthStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.isLoggedIn=true;
          state.user = action.payload;
          state.error = null;
        }
      )
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user = null;
        state.error = null;
      });
  },
});

export default authSlice.reducer;
